<script lang="ts">
import { ComplaintDepartmentInfo, ComplaintInfo } from '@/interface/Complaint.interface';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import Breadcrumb from '@/views/complaint/breadcrumb.vue';
import RequestHeader from '@/components/pages/complaint/detail/header.vue';
import RequestInformation from '@/components/pages/complaint/detail/information.vue';
import AirlineAnswer from '../shared/airlineAnswer.vue';
import Return from './return.vue';
import { BaseConfirm } from '@/interface/BaseConfirm.interface';
import { BaseAlert } from '@/interface/BaseAlert.interface';
import { airlineRequestBreadcrumb } from '@/components/utils';
import { AIRLINE_BTN_KEY, DPM_STATUS, REQUEST_STATUS } from '@/app/const';
import { complaintService, fileService, userService } from '@/services/serviceProvider';
import { AttachmentUploaderType, AttachmentUrl } from '@/app/common/types/attachUploader';
import { UploadResponse } from '@/interface/FileUpload.interface';

export default {
    components: {
        Breadcrumb,
        RequestHeader,
        RequestInformation,
        AirlineAnswer,
        Return
    },
    setup() {
        const router = useRouter();
        const currentRoute = router.currentRoute.value;
        // components
        const airlineAns = ref();
        // pdf
        const pdfContent = ref();

        return { router, currentRoute, airlineAns, pdfContent }
    },
    props: {
        modelValue: {
            type: Object,
            default: {},
        },
    },
    emits: [
        'update:modelValue',
        'displayConfirmDialog',
        'displayAlertDialog',
        'exportPdf',
    ],
    data() {
        return {
            model: this.modelValue as ComplaintInfo,
            breadcrumb: [...airlineRequestBreadcrumb],
            loading: false,
            displayReturnDialog: false,
        }
    },
    watch: {
    },
    computed: {
        someDpmWaitAirline(): boolean {
            if (this.model.relatedMainDepartment?.departmentStatus === DPM_STATUS.WAIT_AIRLINE
                || this.model.relatedSubDepartments?.map(o => o.departmentStatus).includes(DPM_STATUS.WAIT_AIRLINE))
                return true;
            return false;
        },
        filteredBreadcrumb() {
            switch (this.model.status) {
                case REQUEST_STATUS.IN_PROGRESS:
                    if (this.someDpmWaitAirline)
                        return this.breadcrumb;
                    return this.breadcrumb.filter(o => o.key === AIRLINE_BTN_KEY.EXPORT)
                case REQUEST_STATUS.REQUEST_DOC:
                case REQUEST_STATUS.ACTIVE:
                case REQUEST_STATUS.END:
                case REQUEST_STATUS.CANCELLED:
                    return [];
                default:
                    return this.breadcrumb;
            }
        },
        showAirlineAnswer() {
            return this.model.status === REQUEST_STATUS.IN_PROGRESS
                || this.model.status === REQUEST_STATUS.CANCELLED
                || this.model.status === REQUEST_STATUS.END;
        },
        activeDepartment: {
            get() {
                const currentAirline = userService.currentUserInfo()?.username;
                if (this.model.relatedMainDepartment?.external?.externalDepartment === currentAirline)
                    return this.model.relatedMainDepartment;
                else if (this.model.relatedSubDepartments?.map(o => o.external?.externalDepartment).includes(currentAirline)) {
                    return this.model.relatedSubDepartments.find(o => o.external?.externalDepartment === currentAirline);
                }
                return undefined;
            },
            set(newValue: ComplaintDepartmentInfo) {
                const currentAirline = userService.currentUserInfo()?.username;
                if (this.model.relatedMainDepartment?.external?.externalDepartment === currentAirline)
                    this.model.relatedMainDepartment = newValue;
                else if (this.model.relatedSubDepartments?.map(o => o.external?.externalDepartment).includes(currentAirline)) {
                    let sub = this.model.relatedSubDepartments.find(o => o.external?.externalDepartment === currentAirline);
                    if (sub) sub = newValue;
                }
            }
        },
    },
    async mounted() {
    },
    methods: {
        goToListPage() {
            this.router.push({
                path: `/complaint-list`
            })
        },
        displayConfirmDialog(model: BaseConfirm) {
            this.$emit('displayConfirmDialog', model);
        },
        displayAlertDialog(model: BaseAlert) {
            this.$emit('displayAlertDialog', model);
        },
        confirmSave() {
            this.displayConfirmDialog({
                messageTitle: this.$t('t-confirm-save'),
                callBack: this.doAirlineSave
            } as BaseAlert)
        },
        async uploadFileBeforeSave() {
            try {
                let allUploads = [] as AttachmentUploaderType[];
                if (this.activeDepartment?.external?.attachUploads && this.activeDepartment?.external?.attachUploads.length > 0) {
                    if (!this.activeDepartment?.external?.externalDepartmentAttachments) this.activeDepartment.external.externalDepartmentAttachments = [];
                    allUploads = allUploads.concat([...this.activeDepartment?.external?.attachUploads]);
                }

                if (allUploads && allUploads.length > 0) {
                    // prepare payloads
                    let payloads = [] as File[];
                    allUploads.forEach(attach => {
                        if (attach.blob) {
                            const blob = fileService.dataURLToBlob(attach.blob);
                            let file = new File([blob], attach.name, { type: attach.fileType })
                            payloads.push(file);
                        }
                    });
                    // call upload API
                    const res = await fileService.upload(payloads);
                    if (res && res.status === 201) {
                        const data = res.data as UploadResponse;
                        data.files.forEach(file => {
                            let attachment = {
                                itemNo: -1,
                                fileName: file.fileName,
                                runningNo: -1,
                                size: allUploads?.find(upload => upload.name === file.fileName)?.size,
                                url: file.objectName,
                            } as AttachmentUrl;
                            // add to attachment
                            if (this.activeDepartment?.external?.attachUploads?.find(o => o.name === attachment.fileName) !== undefined) {
                                this.activeDepartment?.external?.externalDepartmentAttachments?.push(attachment);
                            }
                        });
                        // clear attach uploads
                        if (this.activeDepartment?.external) {
                            this.activeDepartment.external.attachUploads = [];
                        }
                    } else {
                        throw new Error();
                    }
                }
            } catch (e: any) {
                throw new Error("Upload Error");
            }
        },
        async doAirlineSave() {
            try {
                await this.uploadFileBeforeSave();
                if (this.model.requestNo) {
                    let payloads = {
                        departmentCode: this.activeDepartment?.departmentCode,
                        departmentType: this.activeDepartment?.departmentType,
                        detail: this.activeDepartment?.external?.externalDepartmentAnswer,
                        attachments: this.activeDepartment?.external?.externalDepartmentAttachments,
                    }
                    const res = await complaintService.airlineSave(this.model.requestNo, payloads)
                    if (res && res.status === 200) {
                        this.displayAlertDialog({
                            success: true,
                            messageTitle: this.$t('t-save-success'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    }
                }
            } catch (e: any) {
                if (e.message === 'Upload Error') {
                    this.displayAlertDialog({
                        success: false,
                        messageTitle: this.$t('t-upload-file-fail'),
                    } as BaseAlert)
                } else {
                    this.displayAlertDialog({
                        success: false,
                        messageTitle: this.$t('t-save-fail'),
                    } as BaseAlert)
                }
            }
        },
        async confirmSubmitToDpm() {
            if (this.airlineAns) {
                const valid = await this.airlineAns.validateForm();
                if (valid) {
                    this.displayConfirmDialog({
                        messageTitle: this.$t('t-action-submit-to-dpm-confirm'),
                        callBack: this.doSubmitToDpm
                    } as BaseAlert)
                } else {
                    this.displayAlertDialog({
                        success: false,
                        messageTitle: this.$t('t-complaint-input-require'),
                    } as BaseAlert)
                }
            }
        },
        async doSubmitToDpm() {
            try {
                await this.uploadFileBeforeSave();
                if (this.model.requestNo) {
                    let payloads = {
                        departmentCode: this.activeDepartment?.departmentCode,
                        departmentType: this.activeDepartment?.departmentType,
                        detail: this.activeDepartment?.external?.externalDepartmentAnswer,
                        attachments: this.activeDepartment?.external?.externalDepartmentAttachments,
                    }
                    const res = await complaintService.airlineAnswerToDpm(this.model.requestNo, payloads)
                    if (res && res.status === 200) {
                        this.displayAlertDialog({
                            success: true,
                            messageTitle: this.$t('t-action-submit-to-dpm-success'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    }
                }
            } catch (e: any) {
                if (e.message === 'Upload Error') {
                    this.displayAlertDialog({
                        success: false,
                        messageTitle: this.$t('t-upload-file-fail'),
                    } as BaseAlert)
                } else {
                    this.displayAlertDialog({
                        success: false,
                        messageTitle: this.$t('t-action-submit-to-dpm-fail'),
                    } as BaseAlert)
                }
            }
        },
        async doReturnToDpm(remark: string) {
            try {
                if (this.model.requestNo) {
                    let payloads = {
                        departmentCode: this.activeDepartment?.departmentCode,
                        departmentType: this.activeDepartment?.departmentType,
                        remark: remark
                    }
                    const res = await complaintService.airlineReturnToDpm(this.model.requestNo, payloads)
                    if (res && res.status === 200) {
                        this.displayAlertDialog({
                            success: true,
                            messageTitle: this.$t('t-action-return-to-dpm-success'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    }
                }
            } catch (e: any) {
                this.displayAlertDialog({
                    success: false,
                    messageTitle: this.$t('t-action-return-to-dpm-fail'),
                } as BaseAlert)
            }
        },
        getPdfContent() {
            return this.pdfContent;
        },
    },
};
</script>

<template>
    <div class="bg-page-home main-page"></div>
    <div class="bg-page-home-content complaint-detail app-form-input">
        <Breadcrumb :title="$t('t-complaint')" v-model="filteredBreadcrumb" @do-airline-save="confirmSave"
            @do-submit-to-department="confirmSubmitToDpm" @do-export-pdf="$emit('exportPdf')"
            @do-return-to-department="displayReturnDialog = true">
        </Breadcrumb>
        <div ref="pdfContent" class="pdf-content">
            <RequestHeader v-if="model" v-model="model" :view-mode="true"></RequestHeader>
            <!-- answer from airline -->
            <AirlineAnswer ref="airlineAns" v-if="showAirlineAnswer && activeDepartment?.external"
                v-model="activeDepartment.external" :complaint="model" :uploadable="true"
                :view-mode="!someDpmWaitAirline"></AirlineAnswer>
            <!-- request information -->
            <RequestInformation v-model="model"></RequestInformation>
        </div>
        <Return v-model="displayReturnDialog" :airline-info="model" @on-confirm="doReturnToDpm"></Return>
    </div>
</template>